import { EmployerRule, InputFormula } from '../api/employer-rules'
import { formatDuration } from './date'
import { UserSummaries } from '../api/missions'
import { SummaryData } from '../../../backend/src/services/resources/period-summaries/period-summary.types'
import { getDocIdAsString } from './documents'

/**
 * Récupère la valeur courante d'une règle employeur
 * @param valuesInTime Liste des valeurs dans le temps
 * @returns La valeur courante ou undefined si aucune valeur n'est active
 */
export const getCurrentValue = (
  valuesInTime: EmployerRule['valuesInTime'],
): EmployerRule['valuesInTime'][0]['value'] | undefined =>
  valuesInTime.find(vt => !vt.effectiveTo || new Date(vt.effectiveTo) > new Date())?.value

/**
 * Extrait les valeurs des règles employeur depuis les données de résumé
 *
 * @param employerRules - Liste des règles employeur
 * @param summary - Données de résumé (semaine ou jour)
 * @param durationFormatter - Formateur optionnel pour les durées
 * @returns Objet avec les valeurs des règles indexées par ruleKey
 */
export const getRuleValuesFromSummary = (
  employerRules: UserSummaries['weeks'][0]['employerRules'],
  summary: SummaryData | SummaryData['days'][0],
  durationFormatter?: (duration: number) => string,
): { [key: string]: string | number } =>
  employerRules.reduce<{
    [key: string]: string | number
  }>((rules, employerRule) => {
    const ruleKey = `rule_${employerRule.ruleId}`

    if (employerRule.kind === 'goal') {
      rules[ruleKey] = summary?.employerData?.goals?.[getDocIdAsString(employerRule.ruleId)] || 0
    } else {
      const duration =
        summary?.employerData?.durations?.[getDocIdAsString(employerRule.ruleId)] || 0
      rules[ruleKey] = durationFormatter ? durationFormatter(duration) : formatDuration(duration)
    }
    return rules
  }, {})

/**
 * Recursively extracts all work-period-event IDs referenced by eventPath nodes
 * in an InputFormula AST.
 */
export const extractEventIdsFromFormula = (formula: InputFormula): string[] => {
  if ('op' in formula) {
    return formula.args.flatMap(extractEventIdsFromFormula)
  }
  if ('type' in formula && formula.type === 'eventPath') {
    return formula.workPeriodEventIds
  }
  return []
}

/**
 * Build a map from work-period-event ID to the list of employer rule names
 * that reference it via an eventPath formula node.
 */
export function buildEventRulesMap(employerRules: EmployerRule[]): Map<string, string[]> {
  const map = new Map<string, string[]>()
  for (const rule of employerRules) {
    const eventIds = extractEventIdsFromFormula(rule.interval.inputFormula)
    for (const id of eventIds) {
      const existing = map.get(id) || []
      existing.push(rule.shortName || rule.name)
      map.set(id, existing)
    }
  }
  return map
}
